import { Board, Member } from '@/features/backlog/types';
import { PRIORITIES, SOURCES, TASK_TYPES } from '@/features/backlog/constants';

/**
 * The tiny "SparkQL" query language behind the global search — a much-simplified JQL.
 *
 *   board:Alpha,Beta type:bug priority:high,blocker assignee:me -status:Done  login
 *
 * Space-separated `field:value` tokens are ANDed; commas after a `:` are ORed within the field;
 * a leading `-` (or `!` after the colon) negates a token; anything not a token is free text matched
 * against title + description. Values with spaces go in quotes: `status:"In Review"`.
 *
 * The grammar is resolution-free: parsing only splits the string into raw tokens. Turning names into
 * ids (board/member) and canonical values (type/priority/label) happens in {@link buildSearchRequest}
 * with the app's loaded boards/members, and the backend resolves per-board status labels.
 */

export type SearchField = 'board' | 'status' | 'state' | 'type' | 'priority' | 'assignee' | 'label' | 'source' | 'linked' | 'wiki' | 'completed';

// Fields offered by the autocomplete/chips. `completed` is a date-range facet (FROM..TO) generated by the
// Reports click-through — omitted here so it stays out of the manual autocomplete (you never hand-type it).
export const SEARCH_FIELDS: SearchField[] = ['board', 'status', 'state', 'type', 'priority', 'assignee', 'label', 'source', 'linked', 'wiki'];

/** Field metadata for the help legend + autocomplete headers. */
export const FIELD_META: Record<SearchField, { label: string; example: string; hint: string }> = {
  board: { label: 'board', example: 'board:Alpha,Beta', hint: 'Board name or slug' },
  status: { label: 'status', example: 'status:"In Review"', hint: 'Column name (any board)' },
  state: { label: 'state', example: 'state:open', hint: 'open · done (dropped / won’t fix are neither)' },
  type: { label: 'type', example: 'type:bug', hint: 'Item type' },
  priority: { label: 'priority', example: 'priority:high', hint: 'low · medium · high · blocker' },
  assignee: { label: 'assignee', example: 'assignee:me', hint: 'Member, me, or none' },
  label: { label: 'label', example: 'label:payments', hint: 'Any item label' },
  source: { label: 'source', example: 'source:Customer', hint: 'Item source' },
  linked: { label: 'linked', example: 'linked:SPB-404', hint: 'Items linked to this key' },
  wiki: { label: 'wiki', example: 'wiki:brazil-launch-checklist', hint: 'Items linked to a wiki page (or wiki:any)' },
  completed: { label: 'completed', example: 'completed:2026-08-24..2026-08-31', hint: 'Became done in a date range' },
};

/** Short aliases so power users can type `p:high t:bug b:alpha`. */
const FIELD_ALIASES: Record<string, SearchField> = {
  board: 'board', b: 'board', project: 'board',
  status: 'status', s: 'status',
  state: 'state', is: 'state',
  type: 'type', t: 'type',
  priority: 'priority', prio: 'priority', p: 'priority',
  assignee: 'assignee', who: 'assignee', a: 'assignee',
  label: 'label', tag: 'label', l: 'label',
  source: 'source', src: 'source',
  linked: 'linked', link: 'linked', linkedto: 'linked',
  wiki: 'wiki', wikipage: 'wiki', page: 'wiki',
  completed: 'completed',
};

export interface RawToken {
  field: SearchField;
  values: string[];
  negate: boolean;
}
export interface ParsedQuery {
  text: string;
  tokens: RawToken[];
}

const unquote = (v: string): string => {
  const t = v.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) return t.slice(1, -1);
  return t;
};

/** Split on whitespace while keeping "quoted phrases" together. */
function splitSegments(input: string): string[] {
  const segs: string[] = [];
  let cur = '';
  let inQuote = false;
  for (const ch of input) {
    if (ch === '"') {
      inQuote = !inQuote;
      cur += ch;
      continue;
    }
    if (!inQuote && /\s/.test(ch)) {
      if (cur) segs.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  if (cur) segs.push(cur);
  return segs;
}

/** Split a token's value part on commas, respecting quotes; unquote + drop empties. */
function splitValues(rest: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuote = false;
  for (const ch of rest) {
    if (ch === '"') {
      inQuote = !inQuote;
      cur += ch;
      continue;
    }
    if (ch === ',' && !inQuote) {
      const v = unquote(cur);
      if (v.trim()) out.push(v);
      cur = '';
      continue;
    }
    cur += ch;
  }
  const v = unquote(cur);
  if (v.trim()) out.push(v);
  return out;
}

/** Index of the last comma NOT inside quotes (for quote-aware autocomplete value splitting). */
function lastTopLevelComma(s: string): number {
  let inQuote = false;
  let idx = -1;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '"') inQuote = !inQuote;
    else if (s[i] === ',' && !inQuote) idx = i;
  }
  return idx;
}

/** Resolve a raw segment into either a field token or free text. */
function parseSegment(seg: string): { token?: RawToken; text?: string } {
  let negate = false;
  let s = seg;
  if (s.startsWith('-') && s.length > 1) {
    negate = true;
    s = s.slice(1);
  }
  const colon = s.indexOf(':');
  if (colon > 0) {
    const key = s.slice(0, colon).toLowerCase();
    const field = FIELD_ALIASES[key];
    if (field) {
      let rest = s.slice(colon + 1);
      if (rest.startsWith('!')) {
        negate = true;
        rest = rest.slice(1);
      }
      const values = splitValues(rest);
      if (values.length) return { token: { field, values, negate } };
      return {}; // "field:" with nothing yet — ignore for a run
    }
  }
  // Free text: use the dash-stripped `s` so a leading '-' (unsupported for text) doesn't leak into
  // the term, and quotes are still stripped.
  return { text: unquote(s) };
}

/** Parse a raw query string into free text + typed tokens (no name→id resolution). */
export function parseQuery(input: string): ParsedQuery {
  const tokens: RawToken[] = [];
  const words: string[] = [];
  for (const seg of splitSegments(input)) {
    const { token, text } = parseSegment(seg);
    if (token) tokens.push(token);
    else if (text) words.push(text);
  }
  return { text: words.join(' '), tokens };
}

// ---- compile to the backend request ------------------------------------------------------------

export interface SearchClause {
  field: SearchField;
  values: string[];
  negate: boolean;
}
export interface SearchRequest {
  text: string;
  clauses: SearchClause[];
  limit?: number;
  /** Skip this many matches before the page — for paging past `limit` through the full result set. */
  offset?: number;
}

export interface ResolveCtx {
  boards: Board[];
  members: Member[];
  meId?: string | null;
  /** Known labels (fetched) for case-insensitive canonicalisation of `label:`. */
  labels?: string[];
}

const ci = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** Priority synonyms → canonical value. */
const PRIORITY_SYNONYMS: Record<string, string> = {
  urgent: 'blocker', critical: 'blocker', highest: 'blocker', p0: 'blocker', p1: 'high',
  lowest: 'low', p3: 'low', p2: 'medium', normal: 'medium', med: 'medium',
};

function resolveValue(field: SearchField, raw: string, ctx: ResolveCtx): string {
  const v = raw.trim();
  switch (field) {
    case 'board': {
      const b = ctx.boards.find((x) => ci(x.name, v) || ci(x.slug, v));
      return b ? b.id : v; // unknown board → keeps the raw string (matches nothing, i.e. 0 results)
    }
    case 'assignee': {
      if (ci(v, 'me') || ci(v, 'mine')) return ctx.meId || 'none';
      if (ci(v, 'none') || ci(v, 'unassigned') || ci(v, 'nobody')) return 'none';
      const m = ctx.members.find((x) => ci(x.name, v) || (x.email ? ci(x.email, v) : false));
      return m ? m.id : v;
    }
    case 'type': {
      const t = TASK_TYPES.find((x) => ci(x.value, v) || ci(x.label, v));
      return t ? t.value : v.toLowerCase();
    }
    case 'priority': {
      const p = PRIORITIES.find((x) => ci(x.value, v) || ci(x.label, v));
      if (p) return p.value;
      return PRIORITY_SYNONYMS[v.toLowerCase()] ?? v.toLowerCase();
    }
    case 'label': {
      const known = ctx.labels?.find((l) => ci(l, v));
      return known ?? v; // canonical case so the exact array-overlap match hits
    }
    case 'source': {
      const known = SOURCES.find((sx) => ci(sx, v));
      return known ?? v;
    }
    case 'status':
      return v; // resolved per-board (by label OR value, case-insensitively) on the server
    case 'state': {
      const s = v.toLowerCase();
      if (['done', 'closed', 'complete', 'completed', 'resolved', 'finished'].includes(s)) return 'done';
      if (['open', 'active', 'wip', 'todo', 'notdone', 'not_done', 'ongoing'].includes(s)) return 'open';
      return s; // unknown → passes through (the server ignores it)
    }
    default:
      return v;
  }
}

/** Compile a parsed query into the POST /items/search body, resolving names → ids/values. */
export function buildSearchRequest(parsed: ParsedQuery, ctx: ResolveCtx, limit?: number, offset?: number): SearchRequest {
  const clauses: SearchClause[] = parsed.tokens.map((t) => ({
    field: t.field,
    negate: t.negate,
    values: Array.from(new Set(t.values.map((v) => resolveValue(t.field, v, ctx)).filter(Boolean))),
  })).filter((c) => c.values.length > 0);
  return { text: parsed.text, clauses, limit, offset };
}

/** Serialize a parsed query back into a raw query string (field tokens first, then free text), quoting
 *  any value that contains spaces. Used to rebuild the query after removing an individual filter chip. */
export function stringifyQuery(parsed: ParsedQuery): string {
  const parts = parsed.tokens.map((t) => `${t.negate ? '-' : ''}${t.field}:${t.values.map(quoteIf).join(',')}`);
  const text = parsed.text.trim();
  if (text) parts.push(text);
  return parts.join(' ');
}

/** True when the string has no runnable content (no text and no complete token). */
export function isEmptyQuery(input: string): boolean {
  const p = parseQuery(input);
  return !p.text && p.tokens.length === 0;
}

// ---- autocomplete --------------------------------------------------------------------------------

export type SuggestionCategory = 'field' | SearchField;

export interface Suggestion {
  id: string;
  /** Full text that replaces [replaceStart, end] of the input. */
  insert: string;
  label: string;
  detail?: string;
  category: SuggestionCategory;
  /** Resolved id/value for rich lead rendering (member id, type/priority value…). */
  value?: string;
}

export interface SuggestResult {
  replaceStart: number;
  suggestions: Suggestion[];
}

// Quote a value that contains whitespace OR a comma — both are token/value separators in the grammar,
// so an unquoted one would be mis-split on re-parse (a comma turns one value into an OR of several).
const needsQuote = (v: string) => /[\s,]/.test(v);
const quoteIf = (v: string) => (needsQuote(v) ? `"${v}"` : v);

/** Index where the last whitespace-delimited (quote-aware) segment starts. */
function lastSegmentStart(input: string): number {
  let inQuote = false;
  let start = 0;
  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (ch === '"') inQuote = !inQuote;
    else if (!inQuote && /\s/.test(ch)) start = i + 1;
  }
  return start;
}

/** Suggestions for the tail of the input (cursor assumed at end). */
export function getSuggestions(input: string, ctx: ResolveCtx & { statusLabels?: string[] }, max = 8): SuggestResult {
  const segStart = lastSegmentStart(input);
  let seg = input.slice(segStart);
  let negPrefix = '';
  if (seg.startsWith('-')) {
    negPrefix = '-';
    seg = seg.slice(1);
  }
  const colon = seg.indexOf(':');

  // Typing a value for a known field → suggest that field's values.
  if (colon > 0) {
    const key = seg.slice(0, colon).toLowerCase();
    const field = FIELD_ALIASES[key];
    if (field) {
      const after = seg.slice(colon + 1).replace(/^!/, '');
      const lastComma = lastTopLevelComma(after);
      const prefix = seg.slice(0, colon + 1) + (seg.slice(colon + 1).startsWith('!') ? '!' : '') + (lastComma >= 0 ? after.slice(0, lastComma + 1) : '');
      // The partial being typed — strip a leading quote so a multi-word value (status:"In Rev…) still
      // matches its options, and a leading space after a comma.
      const partial = (lastComma >= 0 ? after.slice(lastComma + 1) : after).replace(/^\s*"?/, '');
      const items = valueSuggestions(field, partial, ctx, max).map((s) => ({
        ...s,
        insert: negPrefix + prefix + quoteIf(s.rawValue) + ' ',
      }));
      return { replaceStart: segStart, suggestions: items };
    }
  }

  // Otherwise → suggest field names matching the partial (or all when empty).
  const partial = seg.toLowerCase();
  const fields = SEARCH_FIELDS.filter((f) => !partial || f.startsWith(partial) || (partial.length === 1 && f[0] === partial));
  const suggestions: Suggestion[] = fields.slice(0, max).map((f) => ({
    id: `field:${f}`,
    insert: negPrefix + f + ':',
    label: `${f}:`,
    detail: FIELD_META[f].hint,
    category: 'field',
  }));
  return { replaceStart: segStart, suggestions };
}

interface RawSuggestion extends Suggestion {
  rawValue: string;
}

function valueSuggestions(field: SearchField, partial: string, ctx: ResolveCtx & { statusLabels?: string[] }, max: number): RawSuggestion[] {
  const p = partial.toLowerCase();
  const match = (s: string) => !p || s.toLowerCase().includes(p);
  const mk = (rawValue: string, label: string, detail?: string, value?: string): RawSuggestion => ({
    id: `${field}:${rawValue}`,
    insert: '', // filled by caller
    rawValue,
    label,
    detail,
    category: field,
    value,
  });

  switch (field) {
    case 'board':
      return ctx.boards.filter((b) => match(b.name) || match(b.slug)).slice(0, max).map((b) => mk(b.name, b.name, b.slug, b.id));
    case 'assignee': {
      const specials: RawSuggestion[] = [];
      if (match('me')) specials.push(mk('me', 'Me', 'Assigned to you', ctx.meId ?? undefined));
      if (match('none') || match('unassigned')) specials.push(mk('none', 'Unassigned', 'No assignee', 'none'));
      const people = [...ctx.members]
        .filter((m) => m.active !== false)
        .filter((m) => match(m.name) || (m.email ? match(m.email) : false))
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice(0, max)
        .map((m) => mk(m.name, m.name, m.email ?? undefined, m.id));
      return [...specials, ...people].slice(0, max);
    }
    case 'type':
      return TASK_TYPES.filter((t) => match(t.label) || match(t.value)).slice(0, max).map((t) => mk(t.value, t.label, undefined, t.value));
    case 'priority':
      return PRIORITIES.filter((pr) => match(pr.label) || match(pr.value)).slice(0, max).map((pr) => mk(pr.value, pr.label, undefined, pr.value));
    case 'label':
      return (ctx.labels ?? []).filter(match).slice(0, max).map((l) => mk(l, l));
    case 'source':
      return SOURCES.filter(match).slice(0, max).map((s) => mk(s, s));
    case 'status':
      return (ctx.statusLabels ?? []).filter(match).slice(0, max).map((s) => mk(s, s));
    case 'state':
      return [
        { v: 'open', l: 'Open', d: 'Not done, dropped or won’t fix' },
        { v: 'done', l: 'Done', d: 'In a done status' },
      ]
        .filter((o) => match(o.v) || match(o.l))
        .map((o) => mk(o.v, o.l, o.d));
    default:
      return [];
  }
}
